import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import UserAvatar from 'modules/core/components/UserAvatar'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { periodEmployee } from 'modules/attendance/lib/periodEmployee'
import type Period from 'modules/attendance/store/models/Period'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'

const getEmployeeUser = (employee: Employee) => {
  const userId = employee.attributes.access_id
  const user = users.get(userId)
  if (!user) {
    throw Error(`Error getting users with id = ${userId}`)
  }
  return user
}

interface CompensationResult {
  employee_id: number
  error?: string
}

export interface CompensationReponse {
  successes: CompensationResult[]
  errors: CompensationResult[]
}

interface ErrorItemProps {
  error: CompensationResult
  selected: Period[]
}

const getErrorText = (error: string, employee: string) => {
  switch (error) {
    case 'closed_policy_period':
      return i18n.t(
        'time_tracking.modals.compensation.payroll.response.errors.closed_policy_period',
        {
          employee,
        }
      )
    case 'destroy_error':
      return i18n.t('time_tracking.modals.compensation.payroll.response.errors.destroy_error', {
        employee,
      })
    case 'no_mapping':
      return i18n.t('time_tracking.modals.compensation.payroll.response.errors.no_mapping', {
        employee,
      })
    case 'no_overtime':
      return i18n.t('time_tracking.modals.compensation.payroll.response.errors.no_overtime', {
        employee,
      })
    case 'no_payroll_pool':
      return i18n.t('time_tracking.modals.compensation.payroll.response.errors.no_payroll_pool', {
        employee,
      })
    case 'no_policy_period':
      return i18n.t('time_tracking.modals.compensation.payroll.response.errors.no_policy_period', {
        employee,
      })
    case 'not_assigned_to_any_policy':
      return i18n.t(
        'time_tracking.modals.compensation.payroll.response.errors.not_assigned_to_any_policy',
        {
          employee,
        }
      )
    case 'source_id_error':
      return i18n.t('time_tracking.modals.compensation.payroll.response.errors.source_id_error', {
        employee,
      })
    case 'not_same_period':
      return i18n.t('time_tracking.modals.compensation.payroll.response.errors.not_same_period', {
        employee,
      })
    case 'unauthorized_to_create_supplement':
      return i18n.t(
        'time_tracking.modals.compensation.payroll.response.errors.unauthorized_to_create_supplement',
        {
          employee,
        }
      )
    case 'unauthorized_to_delete_supplement':
      return i18n.t(
        'time_tracking.modals.compensation.payroll.response.errors.unauthorized_to_delete_supplement',
        {
          employee,
        }
      )
    case 'already_compensated':
      return i18n.t(
        'time_tracking.modals.compensation.payroll.response.errors.already_compensated',
        {
          employee,
        }
      )
    default:
      throw new Error(`Unknown error: ${error}`)
  }
}

const ErrorItem = ({ error, selected }: ErrorItemProps) => {
  const employee_period = selected.find(
    (period) => period.attributes.employee_id == error.employee_id
  )

  if (!employee_period) return <></>

  const employee = periodEmployee(employee_period, employees)

  return (
    <Box
      data-testid="employee-error"
      flexDirection="row"
      alignItems="center"
      gap="s16"
      padding="s16"
    >
      <UserAvatar size="s40" user={getEmployeeUser(employee)} />
      <Text.Normal weight="medium">
        {getErrorText(error.error ?? '', getEmployeeUser(employee).preferredName(employee))}
      </Text.Normal>
    </Box>
  )
}

export default observer(ErrorItem)
